import type { ReactNode } from 'react';

import { OrganizationMember, UserInfo } from '@cartwhl/common/types';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Text from '@cartwhl/ui/base/Text';
import HelpIcon from '@cartwhl/ui/icons/HelpIcon';
import Button from '@cartwhl/ui/input/Button';

export type TeamMemberListItem =
  | {
      kind: 'unassigned';
      plan: 'Professional' | 'Unlimited';
      key: string;
      accessLevel: undefined;
    }
  | {
      kind: 'member';
      plan: 'Professional' | 'Unlimited' | 'Free';
      user: OrganizationMember;
      key: string;
      accessLevel: string;
    };

export default function TeamMemberRow({
  item,
  onAssign,
  onEdit,
  hasEditAccess,
}: {
  item: TeamMemberListItem;
  onAssign?: (plan: 'Professional' | 'Unlimited') => void;
  onEdit?: (member: OrganizationMember) => void;
  hasEditAccess: boolean;
}) {
  let leftCell: ReactNode;
  let actionCell: ReactNode;

  if (item.kind === 'unassigned') {
    leftCell = (
      <div className="flex flex-row items-center gap-2 self-center">
        <HelpIcon />
        <Text as="p" variant="studio/body/sm">
          Unassigned
        </Text>
      </div>
    );
    actionCell = hasEditAccess ? (
      <div className="flex items-center justify-end">
        <Button
          disabled={!hasEditAccess}
          size="sm"
          variant="primary"
          onClick={() => onAssign?.(item.plan)}
        >
          <Text as="p" variant="studio/body/medium/xs">
            Assign
          </Text>
        </Button>
      </div>
    ) : null;
  } else {
    const member = item.user;
    const name =
      member.preferredName ||
      `${member.firstName} ${member.lastName}`.trim() ||
      member.email;

    leftCell = (
      <div className="flex flex-row items-center gap-2 self-center">
        <Avatar userInfo={member as any as UserInfo} size={5} />
        <div className="flex flex-col">
          <Text as="p" variant="studio/body/sm">
            {name}
          </Text>
          <Text
            as="p"
            variant="studio/body/xs"
            className="opacity-60 dark:opacity-70"
          >
            {member.email}
          </Text>
        </div>
      </div>
    );
    actionCell = hasEditAccess ? (
      <div className="flex items-center justify-end">
        <Button
          disabled={!hasEditAccess}
          size="sm"
          variant="secondary"
          onClick={() => onEdit?.(member)}
        >
          Edit
        </Button>
      </div>
    ) : null;
  }

  return (
    <>
      <div className="border-gray-30 dark:border-gray-60 col-span-4 border-t" />
      {leftCell}
      <Text
        as="p"
        variant="studio/body/md"
        className="self-center opacity-60 dark:opacity-70"
      >
        {item.plan}
      </Text>
      <Text
        as="p"
        variant="studio/body/md"
        className="self-center opacity-60 dark:opacity-70"
      >
        {item.accessLevel}
      </Text>
      {actionCell}
    </>
  );
}
